import React, { useCallback, useEffect } from 'react';
import T from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'web-components';
import { MenuItem } from '@mui/material';

import { loadUsers } from '../../../../redux/users/actions';
import { getUsers } from '../../../../redux/rootSelectors';
import { getActiveUsersAsOptions, getCommonLoadingState } from '../../../../helpers/utils';
import SensorNotificationSettings from './SensorNotificationSettings';
import { getMachineNotificationRules, getSelectedMachine } from '../../../../redux/machines/selectors';
import { loadMachine, loadMachineNotificationRules } from '../../../../redux/machines/actions';
import { isMachineNotFound } from '../../../../helpers/machineErrors';

const ConnectedSensorNotificationSettings = ({ machineId, sensor, menu, onMenuClick, isMobile }) => {
  const dispatch = useDispatch();
  const { loadingState, data } = useSelector(getUsers);
  const { loadingState: machineLoadingState, data: machineData } = useSelector(getSelectedMachine);
  const {
    loadingState: notificationRuleLoadingState,
    data: notificationRules,
    updateLoadingState: notificationUpdateState,
    deleteLoadingState: notificationDeleteState
  } = useSelector(getMachineNotificationRules);

  const loadNeededData = useCallback(() => {
    dispatch(loadUsers());
    dispatch(loadMachine(machineId));
    dispatch(loadMachineNotificationRules(machineId, sensor.type));
  }, [dispatch, machineId, sensor.type]);

  useEffect(() => {
    loadNeededData();
  }, [loadNeededData]);

  function convertRules(oldObj) {
    let newObj = {};

    if (oldObj.metric_config.comparison_type === 'LTE') {
      newObj = {
        id: oldObj.id,
        machine_id: machineId,
        metric_type: oldObj.metric_config.metric_type,
        warning: {
          min: oldObj.metric_config.value
        },
        'e-mail_user_ids': oldObj?.target_users?.filter(u => u.notification_medium === 'EMAIL').map(u => u.id),
        sms_user_ids: oldObj?.target_users?.filter(u => u.notification_medium === 'SMS').map(u => u.id)
      };
    }

    if (oldObj.metric_config.comparison_type === 'GTE') {
      newObj = {
        id: oldObj.id,
        machine_id: machineId,
        metric_type: oldObj.metric_config.metric_type,
        warning: {
          max: oldObj.metric_config.value
        },
        'e-mail_user_ids': oldObj?.target_users?.filter(u => u.notification_medium === 'EMAIL').map(u => u.id),
        sms_user_ids: oldObj?.target_users?.filter(u => u.notification_medium === 'SMS').map(u => u.id)
      };
    }
    return newObj;
  }

  const minNotificationRule = notificationRules.find(rule => rule?.metric_config?.comparison_type === 'LTE');
  const maxNotificationRule = notificationRules.find(rule => rule?.metric_config?.comparison_type === 'GTE');

  const notifyRules = {
    min: minNotificationRule ? convertRules(minNotificationRule) : undefined,
    max: maxNotificationRule ? convertRules(maxNotificationRule) : undefined
  };

  return (
    <SensorNotificationSettings
      loadingState={
        isMachineNotFound(machineLoadingState)
          ? machineLoadingState
          : getCommonLoadingState([loadingState, machineLoadingState, notificationRuleLoadingState])
      }
      loadingComponent={() =>
        menu ? (
          <MenuItem>
            <Loader size={15} color="primary" />
          </MenuItem>
        ) : (
          <Loader size={20} />
        )
      }
      reloadAction={loadNeededData}
      users={getActiveUsersAsOptions(data)}
      machine={machineData}
      menu={menu}
      onMenuClick={onMenuClick}
      sensor={sensor}
      notificationRules={notifyRules}
      notificationUpdateState={notificationUpdateState}
      notificationDeleteState={notificationDeleteState}
      isMobile={isMobile}
    />
  );
};

ConnectedSensorNotificationSettings.propTypes = {
  machineId: T.string.isRequired,
  sensor: T.shape({ type: T.string }).isRequired,
  menu: T.bool,
  onMenuClick: T.func,
  isMobile: T.bool
};

ConnectedSensorNotificationSettings.defaultProps = {
  menu: false,
  onMenuClick: null,
  isMobile: false
};

export default ConnectedSensorNotificationSettings;
